import type { CurrentFieldValues } from "../models/data-request.model";

import { formatLocaleDate, formatReductionImpact, formatUnitLabel, formatYearlyCosts } from "./field-format";
import { toDisplayProjectDate } from "./project-date-offset";
import { analyzeDescription } from "./tiptap-text";

interface FieldValueAccessor {
  display: (fieldValues: CurrentFieldValues, locale: string) => string | null;
  raw: (fieldValues: CurrentFieldValues) => string | null;
}

const FIELD_VALUE_ACCESSORS: Record<string, FieldValueAccessor> = {
  title: {
    display: (fv) => fv.title ?? null,
    raw: (fv) => fv.title ?? null,
  },
  description: {
    display: (fv) => analyzeDescription(fv.description)?.displayText ?? null,
    raw: (fv) => analyzeDescription(fv.description)?.rawValue ?? null,
  },
  status: {
    display: (fv) => fv.status?.name ?? null,
    raw: (fv) => fv.status?.id ?? null,
  },
  priority: {
    display: (fv) => fv.priority?.name ?? null,
    raw: (fv) => fv.priority?.id ?? null,
  },
  phase: {
    display: (fv) => fv.phase?.name ?? null,
    raw: (fv) => fv.phase?.id ?? null,
  },
  focusArea: {
    display: (fv) => fv.focusArea?.name ?? null,
    raw: (fv) => fv.focusArea?.id ?? null,
  },
  assignee: {
    display: (fv) => {
      const list = fv.assignee;
      if (!list || list.length === 0) return null;
      return list.map((a) => a.fullname ?? a.email ?? "").filter(Boolean).join(", ") || null;
    },
    raw: (fv) => {
      const list = fv.assignee;
      if (!list || list.length === 0) return null;
      return list.map((a) => a.id).filter(Boolean).join(",") || null;
    },
  },
  labels: {
    display: (fv) => {
      const list = fv.labels;
      if (!list || list.length === 0) return null;
      return list.map((l) => l.title).filter(Boolean).join(", ") || null;
    },
    raw: (fv) => {
      const list = fv.labels;
      if (!list || list.length === 0) return null;
      return list.map((l) => l.id).filter(Boolean).join(",") || null;
    },
  },
  startDate: {
    display: (fv, locale) => formatLocaleDate(toDisplayProjectDate(fv.startDate), locale),
    raw: (fv) => (fv.startDate ? String(fv.startDate) : null),
  },
  endDate: {
    display: (fv, locale) => formatLocaleDate(toDisplayProjectDate(fv.endDate), locale),
    raw: (fv) => (fv.endDate ? String(fv.endDate) : null),
  },
  yearlyCosts: {
    display: (fv, locale) => formatYearlyCosts(fv.yearlyCosts, locale),
    raw: (fv) =>
      fv.yearlyCosts !== null && fv.yearlyCosts !== undefined ? String(Math.round(fv.yearlyCosts * 100) / 100) : null,
  },
  reductionImpact: {
    display: (fv, locale) => formatReductionImpact(fv.reductionImpact, locale),
    raw: (fv) =>
      fv.reductionImpact !== null && fv.reductionImpact !== undefined
        ? String(Math.round(fv.reductionImpact * 1000) / 1000)
        : null,
  },
  indicatorTitle: {
    display: (fv) => fv.indicatorTitle ?? null,
    raw: (fv) => fv.indicatorTitle ?? null,
  },
  indicatorDescription: {
    display: (fv) => analyzeDescription(fv.indicatorDescription)?.displayText ?? null,
    raw: (fv) => analyzeDescription(fv.indicatorDescription)?.rawValue ?? null,
  },
  unit: {
    display: (fv) => (fv.unit ? formatUnitLabel(fv.unit.title, fv.unit.symbol) : null),
    raw: (fv) => fv.unit?.id ?? null,
  },
  frequency: {
    display: (fv) => fv.frequency ?? null,
    raw: (fv) => fv.frequency ?? null,
  },
};

export function getDisplayValue(fieldValues: CurrentFieldValues, fieldKey: string, locale: string): string | null {
  return FIELD_VALUE_ACCESSORS[fieldKey]?.display(fieldValues, locale) ?? null;
}

export function getRawValue(fieldValues: CurrentFieldValues, fieldKey: string): string | null {
  return FIELD_VALUE_ACCESSORS[fieldKey]?.raw(fieldValues) ?? null;
}
